import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { papi } from "@/lib/papi";
import { apiError } from "@/lib/api";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldHalf, Loader2, AlertCircle, ServerCog } from "lucide-react";

export default function PlatformLogin() {
  const { status, login } = usePlatformAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (status === "authed") return <Navigate to="/platform" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await papi.post("/platform/login", { email, password });
      login(data.token, data.admin);
      navigate("/platform");
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="dark flex min-h-[100dvh] items-center justify-center bg-[hsl(var(--sidebar))] px-5"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="platform-login-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground"><ShieldHalf className="h-6 w-6" /></span>
          <div>
            <div className="text-base font-semibold text-white">Plattform-Administration</div>
            <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--sidebar-muted))]">primeNetConnect ERP · System-Inhaber</div>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-xl font-semibold tracking-tight text-white">System-Inhaber Anmeldung</h2>
          <p className="mt-1 text-xs text-[hsl(var(--sidebar-muted))]">Getrennter, plattformweiter Zugang. Kein Einblick in Kundendaten.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {error && <Alert variant="destructive" data-testid="platform-login-error"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-1.5">
              <Label htmlFor="pemail" className="text-[hsl(var(--sidebar-foreground))]">E-Mail</Label>
              <Input id="pemail" type="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11 border-white/10 bg-white/5 text-white placeholder:text-white/30" data-testid="platform-email-input" placeholder="system@primenetconnect" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ppw" className="text-[hsl(var(--sidebar-foreground))]">Passwort</Label>
              <Input id="ppw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11 border-white/10 bg-white/5 text-white placeholder:text-white/30" data-testid="platform-password-input" placeholder="••••••••" />
            </div>
            <Button type="submit" className="h-11 w-full bg-accent text-accent-foreground hover:bg-accent/90" disabled={loading} data-testid="platform-login-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Anmelden
            </Button>
          </form>
        </div>
        <a href="/login" className="mt-6 flex items-center justify-center gap-1.5 text-xs text-[hsl(var(--sidebar-muted))] hover:text-white">
          <ServerCog className="h-3.5 w-3.5" /> Zur normalen Mandanten-Anmeldung
        </a>
      </div>
    </div>
  );
}
